import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { BookOpen, ArrowLeft, FileText, CreditCard, RefreshCw, AlertTriangle } from 'lucide-react';

const TermsOfSale = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center text-blue-600 hover:text-blue-700">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Book Review Specialists Logo"
                className="w-6 h-6 rounded-full object-cover mr-2"
              />
              <span className="font-semibold">Book Review Specialists</span>
            </Link>
            <Link to="/" className="btn btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <FileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-slate-800 mb-4">Terms of Sale</h1>
            <p className="text-lg text-slate-600">
              Please read these terms carefully before purchasing our courses.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Last updated: January 2025
            </p>
          </div>

          <div className="space-y-8">
            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Agreement to Terms</h2>
              <p className="text-slate-600 mb-4">
                By purchasing any course or service from Book Review Specialists ("we," "us," or "our"), you ("Customer" or "you") agree to be bound by these Terms of Sale. These terms constitute a legally binding agreement between you and Book Review Specialists.
              </p>
              <p className="text-slate-600">
                If you do not agree to these terms, please do not purchase our courses or services.
              </p>
            </Card>

            <Card className="card">
              <div className="flex items-start mb-4">
                <CreditCard className="w-6 h-6 text-green-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">Course Offerings and Pricing</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Course Structure</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li><strong>Enrollment Fee:</strong> £9.99 - Secures your place and provides access to introductory materials</li>
                    <li><strong>Foundation Course:</strong> £75.00 - 6 comprehensive modules covering essential skills</li>
                    <li><strong>Pro Course:</strong> £215.00 - Foundation content plus 5 advanced business modules</li>
                  </ul>
                  <p className="text-slate-600 mt-2">
                    <strong>Enrollment Bonus:</strong> All students who pay the enrollment fee receive instant access to download 
                    "The Ultimate Guide to Selling More Ebooks" report by Diane Shawe.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Payment Options</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Pay enrollment fee first, then select and pay for your preferred course</li>
                    <li>Complete package payment (enrollment + course) in one transaction</li>
                    <li>All payments processed securely through Square</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Course Scheduling</h3>
                  <p className="text-slate-600">
                    Courses start every two months with specific enrollment periods. Once you select a start date, 
                    you will receive all course materials and access according to the published schedule.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Payment Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Payment Processing</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>All payments are processed in British Pounds (GBP)</li>
                    <li>Payment is due in full at the time of purchase</li>
                    <li>We accept payments through Square (credit cards, debit cards, and digital wallets)</li>
                    <li>Your access to course materials begins after successful payment verification</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Billing Information</h3>
                  <p className="text-slate-600">
                    You are responsible for providing accurate billing information. Any disputes regarding charges 
                    must be reported within 30 days of the transaction date.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="card">
              <div className="flex items-start mb-4">
                <RefreshCw className="w-6 h-6 text-blue-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">Refund Policy</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Refund Policy</h3>
                  <p className="text-slate-600 mb-2">
                    <strong>All sales are final.</strong> Due to the digital nature of our courses and immediate access to valuable content, we do not offer refunds under any circumstances.
                  </p>
                  <p className="text-slate-600 mb-2">
                    By completing your purchase, you acknowledge that:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>All course materials are delivered digitally and accessible immediately upon payment</li>
                    <li>Once course modules are downloaded or accessed, no refunds will be issued</li>
                    <li>This includes enrollment fees, course fees, and any additional materials</li>
                    <li>You have read and understood the course description and content before purchasing</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Digital Content Policy</h3>
                  <p className="text-slate-600 mb-2">
                    Our courses provide immediate value through:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Instant access to professional video book review training</li>
                    <li>Downloadable course materials and resources</li>
                    <li>Proprietary AI tools and strategies</li>
                    <li>Business templates and certification materials</li>
                  </ul>
                  <p className="text-slate-600 mt-2">
                    Due to the nature of digital products and the immediate value provided, all purchases are considered final.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Customer Support</h3>
                  <p className="text-slate-600 mb-2">
                    While we do not offer refunds, we are committed to your success:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Full technical support for accessing course materials</li>
                    <li>Assistance with course content and implementation</li>
                    <li>Community support through our Facebook group</li>
                    <li>Direct email support for course-related questions</li>
                  </ul>
                  <p className="text-slate-600 mt-2">
                    Contact our support team at info@virtualautochat.uk for any assistance with your course.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Course Access and Delivery</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Digital Delivery</h3>
                  <p className="text-slate-600">
                    All courses are delivered digitally through our online platform. You will receive login credentials 
                    and access instructions via email after successful payment.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Lifetime Access</h3>
                  <p className="text-slate-600">
                    Purchased courses include lifetime access to materials, updates, and resources. However, 
                    we reserve the right to discontinue courses with 90 days' notice.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Technical Requirements</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Reliable internet connection</li>
                    <li>Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                    <li>Email account for communications and updates</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Intellectual Property</h2>
              <div className="space-y-4">
                <p className="text-slate-600">
                  All course materials, including videos, documents, templates, and resources, are the intellectual 
                  property of Book Review Specialists and are protected by copyright laws.
                </p>
                
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Permitted Use</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Personal use for learning and skill development</li>
                    <li>Implementation of strategies in your own business</li>
                    <li>Reference for your professional development</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Prohibited Use</h3>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Sharing, copying, or distributing course materials</li>
                    <li>Reselling or sublicensing content to third parties</li>
                    <li>Creating derivative works based on our materials</li>
                    <li>Reverse engineering or copying our teaching methods</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Certification</h2>
              <p className="text-slate-600 mb-4">
                Upon successful completion of course requirements, you will receive a digital certificate 
                of completion from Book Review Specialists.
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Certificates are awarded upon completion of all required modules</li>
                <li>Certificates are non-transferable and tied to your enrollment</li>
                <li>We maintain records of all issued certifications</li>
                <li>Certificates may be verified through our support team</li>
              </ul>
            </Card>

            <Card className="card">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">Disclaimers and Limitations</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Income Guarantees</h3>
                  <p className="text-slate-600">
                    While our courses provide valuable skills and strategies, we make no guarantees about your 
                    potential earnings or success. Results depend on your effort, market conditions, and individual circumstances.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Educational Purpose</h3>
                  <p className="text-slate-600">
                    Our courses are for educational purposes only. We are not liable for any business decisions 
                    or actions you take based on course content.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Limitation of Liability</h3>
                  <p className="text-slate-600">
                    Our liability is limited to the amount you paid for the course. We are not responsible for 
                    any indirect, incidental, or consequential damages.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Changes to Terms</h2>
              <p className="text-slate-600">
                We reserve the right to modify these terms at any time. Changes will be posted on this page 
                with an updated effective date. Continued use of our services after changes constitutes acceptance 
                of the new terms.
              </p>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Governing Law</h2>
              <p className="text-slate-600">
                These terms are governed by the laws of the United Kingdom. Any disputes will be resolved 
                through binding arbitration or in the courts of the United Kingdom.
              </p>
            </Card>

            <Card className="card bg-blue-50 border-blue-200">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Contact Information</h2>
              <p className="text-slate-600 mb-4">
                For questions about these terms or our courses, please contact us:
              </p>
              <div className="space-y-2 text-slate-600">
                <p><strong>Email:</strong> info@virtualautochat.uk</p>
                <p><strong>Website:</strong> bookreviewspecialists.app</p>
                <p><strong>Support:</strong> Available Monday-Friday, 9 AM - 5 PM GMT</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfSale;